/**
 * BaselineList.jsx
 * Renders a paginated, sortable list/table of Baselines.
 * Used in the Baseline module main page.
 * - Data source: Redux state (baselines.list)
 * - Supports sorting, dense mode, pagination, status toggling, and row selection
 * - Intended to match RAVEN design patterns for table/list modules
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Parser } from "html-to-react";
import PropTypes from "prop-types";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useListTableState } from "src/hooks/useListTableState";

// Layout/UI
import {
	Box,
	ButtonGroup,
	Checkbox,
	createListCollection,
	IconButton,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Pagination,
	Portal,
	Switch,
	Table,
	MetreaTooltip,
} from "@metrea-ui/v2";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import styles from "./BaselineList.module.css";
import { EnhancedTableHead, EnhancedTableToolbar } from "src/components/UI/common/EnhancedTableParts";
import CardList from "src/components/UI/CardList/CardList";
import { ChevronLeftRegular, ChevronRightRegular } from "@fluentui/react-icons";

// Utilities
import { baselineAccess, extendBaseline } from "src/utils/baselineUtils";
import { Session, isDevTeam, isDeveloper } from "src/utils/sessionUtils";
import { debugLog, showTesting, isDevSiteNow } from "src/utils/debugUtils";
import { getShortDateTimeString, getShortDateString, highlight } from "src/utils/formatUtils";

const SEARCH_FIELDS = ["name"];

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const statusCollection = createListCollection({
	items: [
		{ label: "Inactive", value: "0", rawValue: 0 },
		{ label: "Active", value: "1", rawValue: 1 },
	],
});

const BaselineList = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const pageMode = props.mode;
	const uiSize = props.size;
	const htmlParser = new Parser();

	const { allUserGroups } = useLookupsData();

	// --- Redux Selectors ---
	const baselines = useSelector((state) => state.baselines?.list ?? []);

	// Filter by user's access
	const filterBaselines = baselines
		.filter((x) => baselineAccess(x, allUserGroups, Session().currUser) >= 1)
		.map(extendBaseline);

	// --- Local State ---
	const [showInactive, setShowInactive] = useState(false);
	const [itemSelected, setItemSelected] = useState([]);
	const [searchKeyword, setSearchKeyword] = useState("");

	useEffect(() => {
		debugLog(2, "[BaselineList][FLOW] mounted", {});
	}, []);

	// Helper to return filtered baselines based on Active/Inactive toggle.
	const visibleBaselines = useMemo(() => {
		const activeFiltered = showInactive
			? filterBaselines
			: filterBaselines.filter((x) => x.status_id === 1);
		if (!searchKeyword) return activeFiltered;

		const kw = searchKeyword.toLowerCase();
		const result = activeFiltered.filter((u) =>
			SEARCH_FIELDS.some((field) => {
				const val = u[field];
				return typeof val === "string" && val.toLowerCase().includes(kw);
			}),
		);
		debugLog(4, "[BaselineList][PROCESS] visibleBaselines filter fields", {
			searchFields: SEARCH_FIELDS,
		});

		return result;
	}, [showInactive, filterBaselines, searchKeyword]);

	// --- Controlled selection handler (notifies parent on user change only)
	const handleSelectionChange = (newSelected) => {
		debugLog(3, "[BaselineList][ACTION] handleSelectionChange", {
			selectedCount: newSelected?.length ?? 0,
		});
		setItemSelected(newSelected);
	};

	const headCells = [
		{
			id: "name",
			numeric: false,
			disablePadding: false,
			label: "Name",
		},
		{
			id: "periodLabel",
			numeric: false,
			disablePadding: false,
			label: "Period",
			disableSort: true,
		},
		{
			id: "currency_symbol",
			numeric: false,
			disablePadding: false,
			label: "Currency Symbol",
		},
		{
			id: "createdAt",
			numeric: false,
			disablePadding: false,
			label: "Date Created",
		},
		{
			id: "updatedAt",
			numeric: false,
			disablePadding: false,
			label: "Last Updated",
		},
		{
			id: "scenario_count",
			numeric: false,
			disablePadding: false,
			label: "Scenarios #",
			disableSort: true,
		},

		{
			id: "number_of_active_items",
			numeric: false,
			disablePadding: false,
			label: "Active Items #",
		},
		{
			id: "status_id",
			numeric: false,
			disablePadding: false,
			label: "Status",
			align: "center",
		},
	];
	if (showTesting() && isDevTeam()) {
		headCells.unshift({
			id: "uuid",
			numeric: false,
			disablePadding: false,
			label: "UUID",
		});
	}

	// Custom hooks
	const {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		// page,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		selected,
		// setSelected,
		visibleRows,
		emptyRows,
		handleRequestSort,
		// handleChangePage,
		handleChangePageM1,
		// handleChangeRowsPerPage,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	} = useListTableState({
		// items: props.items, // Or from Redux selector
		items: visibleBaselines, // Or from Redux selector
		// initialOrderBy: "updatedAt",
		initialOrderBy: "updatedAtTS", // ← sort on numeric timestamp
		initialOrder: "desc",
		initialRowsPerPage: 10,
		initialPage: 1,
		selectionMode: true,
		selected: itemSelected,
		onSelectedChange: handleSelectionChange,
	});

	// --- Handlers ---
	const rowClickHandler = (event, item) => {
		debugLog(3, "[BaselineList][ACTION] rowClickHandler", { item: item });
		// Always open by numeric id to avoid UUID-collision ambiguity after imports.
		const baselineId = typeof item === "object" && item !== null ? item.id : item;
		props.onSelectBaseline(baselineId);
	};

	const changeStatusHandler = (o, value) => {
		debugLog(3, "[BaselineList][ACTION] changeStatusHandler", {
			id: o.id,
			name: o.name,
			value: value,
			baseline: o,
		});
		debugLog(2, "[BaselineList][FLOW] onChangeStatus started", { id: o.id, value: value });
		props.onChangeStatus(o.id, value);
	};

	// Handlers for RecordNav (sorting, show inactive, etc.)
	const handleSearchChange = (keyword) => {
		setSearchKeyword(keyword);
		debugLog(7, "[BaselineList][ACTION] handleSearchChange", { keyword: keyword });
	};

	const toggleInactiveHandler = (option) => {
		debugLog(3, "[BaselineList][ACTION] toggleInactiveHandler", { option: option });
		setShowInactive(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[BaselineList][ACTION] sortByHandler", { option: option });

		// setOrderBy(option.orderBy);
		const mapOrderBy = (ob) =>
			ob === "updatedAt" ? "updatedAtTS" : ob === "createdAt" ? "createdAtTS" : ob;
		setOrderBy(mapOrderBy(option.orderBy));
		setOrder(option.order);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[BaselineList][ACTION] changeRowsPerPageHandler", { option: option });
		// setRowsPerPage(option.id);
		const next = Number(option?.id);
		if (!Number.isFinite(next) || next <= 0) return;

		setRowsPerPage(next);

		// After per-page changes, page 1 is safest:
		setPage(0);
		setPageM1(1);
	};

	const renderStatusSelect = (row) => (
		<MetreaSelectRoot
			variant="outline"
			collection={statusCollection}
			size={sizeToSelectSize(uiSize)}
			width="105px"
			name="status_id"
			value={[String(row.status_id ?? "")]}
			onValueChange={(details) => {
				const nextValue = Array.isArray(details?.value) ? String(details.value[0] ?? "") : "";
				// Keep the existing status-change contract: the direct Select gives us
				// strings, but downstream handlers still expect numeric 0/1 ids here.
				const normalizedValue = Number.isNaN(Number(nextValue)) ? nextValue : Number(nextValue);
				changeStatusHandler(row, normalizedValue);
			}}
		>
			<MetreaSelectTrigger id={`baseline-status-${row.id}`}>
				<MetreaSelectValueText />
			</MetreaSelectTrigger>
			<Portal>
				<MetreaSelectContent>
					{statusCollection.items.map((item) => (
						<MetreaSelectItem key={item.value} item={item}>
							{item.label}
						</MetreaSelectItem>
					))}
				</MetreaSelectContent>
			</Portal>
		</MetreaSelectRoot>
	);

	// Inside your component (replace the current <Paper>...<Table.Root>...</Paper> block):
	const columns = [
		...(showTesting() && isDevTeam()
			? [
					{
						key: "uuid",
						title: "UUID",
						w: 240,
						render: (row) => row.uuid,
					},
				]
			: []),
		{
			key: "name",
			title: "Name",
			w: 480,
			stackGrow: 2,

			// -------------------------------------------------------
			// Show only the plain Name in the visible cell (no description).
			// -------------------------------------------------------
			// render: (row) => row.name,
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.name, false)),

			// -------------------------------------------------------
			// Decide per row when to display a tooltip:
			// - If there is a description → show custom Name+Note tooltip even when not truncated
			// - If description is empty → fall back to normal "auto" (only show when truncated)
			// -------------------------------------------------------
			tooltip: (row) => {
				const hasDescription = Boolean(row.description?.trim().length > 0);
				return hasDescription ? "always" : "auto";
			},

			// -------------------------------------------------------
			// Provide the text shown inside the tooltip:
			// - If there's a description → show:
			//       "<Name>\n\nNote:\n<Description>"
			// - Otherwise → return undefined so CardList skips rendering tooltip entirely.
			// -------------------------------------------------------
			tooltipText: (row) => {
				const descriptionExists = Boolean(row.description?.trim().length > 0);
				if (!descriptionExists) return undefined;

				const tooltipLines = [
					row.name,
					"", // blank line for spacing
					"Note:",
					row.description,
				];
				// Join with line breaks so EllipsisTooltip will display multiline correctly
				return tooltipLines.join("\n");
			},
		},
		{ key: "periodLabel", title: "Period", minW: 90, tooltip: "auto" },
		{
			key: "currency",
			title: "Currency Symbol",
			minW: 100,
			tooltip: "auto",
			render: (row) => `${row.currency_code} - ${row.currency_symbol} ${row.currency_unit}`,
		},
		{
			key: "createdAt",
			title: "Date Created",
			minW: 80,
			tooltip: "auto",
			render: (row) => getShortDateString(row.createdAt),
		},
		{
			key: "updatedAt",
			title: "Last Updated",
			minW: 120,
			tooltip: "auto",
			render: (row) => getShortDateTimeString(row.updatedAt),
		},
		{
			key: "scenarios",
			title: "Scenarios #",
			minW: 60,
			tooltip: "auto",
			render: (row) => row?.scenarios?.length ?? 0,
		},
		{
			key: "number_of_active_items",
			title: "Active Items #",
			minW: 60,
			tooltip: "auto",
			render: (row) => row?.number_of_active_items ?? 0,
		},
		{
			key: "status_id",
			title: "Status",
			minW: 60,
			tooltip: undefined, // or omit the field to disable the ellipsis tooltip path
			render: (row) => (row?.status_id === 1 ? "Active" : "Inactive"),
		},
	];

	// Map your RecordNav controls to CardList's toolbar
	// Don't need this - use RecordNav instead
	// const toolbar = {
	// 	showInactive: { value: showInactive, onChange: toggleInactiveHandler, label: "Show inactive" },
	// 	sortBy: {
	// 		valueId: `${orderBy}:${order}`, // e.g., "updatedAt:desc"
	// 		onChange: (opt) => {
	// 			const [ob, dir] = (opt.id || "").split(":");
	// 			sortByHandler({ orderBy: ob || "updatedAt", order: dir || "desc" });
	// 		},
	// 		options: [
	// 			{ id: "updatedAt:desc", label: "Update Date (desc)" },
	// 			{ id: "updatedAt:asc", label: "Update Date (asc)" },
	// 			{ id: "createdAt:desc", label: "Create Date (desc)" },
	// 			{ id: "createdAt:asc", label: "Create Date (asc)" },
	// 			{ id: "name:asc", label: "Name (A→Z)" },
	// 			{ id: "name:desc", label: "Name (Z→A)" },
	// 		],
	// 	},
	// 	pageSize: {
	// 		valueId: String(rowsPerPage),
	// 		onChange: (opt) => changeRowsPerPageHandler({ id: Number(opt.id) }),
	// 		options: [
	// 			{ id: "10", label: "10" },
	// 			{ id: "25", label: "25" },
	// 			{ id: "50", label: "50" },
	// 		],
	// 	},
	// 	// actions: [{ label: "Upload", onClick: onUpload, icon: <UploadIcon/> }, ...] // optional
	// };

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[BaselineList][FLOW] render", {
		renderCount: renderCount.current,
		baselines: baselines,
	});
	debugLog(9, "[BaselineList][FLOW] renderState", {
		props: props,
		visibleRows: visibleRows,
		visibleBaselines: visibleBaselines,
		rowsPerPage: rowsPerPage,
		pageM1: pageM1,
		selected: selected,
		showInactive: showInactive,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<>
			<Box w="100%">
				{/* Replace Table/Paper block with CardList */}
				<CardList
					// title="Baselines"
					// subtitle={`Showing ${visibleRows.length} of ${visibleBaselines.length} results`}
					rows={visibleRows}
					keyField="id"
					columns={columns}
					// onRowClick={(row) => rowClickHandler(null, row)}
					// toolbar={toolbar}
					toolbarSlot={
						<RecordNav
							data={visibleBaselines}
							showSearch={isDeveloper()}
							searchFields={SEARCH_FIELDS}
							columns={columns}
							onSearchChange={handleSearchChange}
							showToggleInactive
							showSortBy
							showRowsPerPage
							rowsPerPage={rowsPerPage}
							count={visibleRows.length}
							onToggleInactive={toggleInactiveHandler}
							onSortBy={(opt) => sortByHandler({ orderBy: opt.orderBy, order: opt.order })}
							onChangeRowsPerPage={(opt) => changeRowsPerPageHandler(opt)}
							callFrom="Listing"
							orderBy={orderBy} // may be "updatedAtTS" / "createdAtTS" / "name" / "status_id"
							order={order}
						/>
					}
					showLabels
					density="compact"
					responsive={{
						mode: "auto", // enable automatic multi-row
						maxRows: 5, // try 3 rows for mobile; can be 4/5 if needed
						minColWidth: 100, // tune to your columns
						gap: 12, // column gap
					}}
					showVerticalDividers
					// pagination={{
					// 	page: pageM1, // your 1-based page
					// 	pageCount: Math.max(1, Math.ceil(visibleBaselines.length / rowsPerPage)),
					// 	onPageChange: handleChangePageM1,
					// }}
					paginationSlot={
						<Pagination.Root
							count={visibleBaselines.length}
							pageSize={Number(rowsPerPage) || 10}
							page={pageM1}
							onPageChange={({ page }) => handleChangePageM1(undefined, page)}
						>
							<ButtonGroup variant="ghost" size="2xs">
								<Pagination.PrevTrigger asChild>
									<IconButton>
										<ChevronLeftRegular />
									</IconButton>
								</Pagination.PrevTrigger>
								<Pagination.Items
									render={(page) => (
										<IconButton variant={{ base: "ghost", _selected: "solid" }}>
											{page.value}
										</IconButton>
									)}
								/>
								<Pagination.NextTrigger asChild>
									<IconButton>
										<ChevronRightRegular />
									</IconButton>
								</Pagination.NextTrigger>
							</ButtonGroup>
						</Pagination.Root>
					}
					rowActions={(row) => {
						const actions = [];
						actions.push({
							id: "open",
							label: "Open",
							onSelect: (row) => rowClickHandler(null, row),
						});

						const isActive = String(row.status_id) === "1";
						const canEdit =
							pageMode === "edit" &&
							baselineAccess(row, allUserGroups, Session().currUser) === 2;
						if (canEdit) {
							actions.push({
								id: "toggle-active",
								label: isActive ? "Make Inactive" : "Make Active",
								onSelect: () => changeStatusHandler(row, isActive ? 0 : 1),
							});
						}

						// actions.push({
						// 	id: "edit",
						// 	label: "Edit",
						// 	// onSelect: () => navigate(`/baseline/${row.id}/edit`),
						// });

						return actions;
					}}
				/>
			</Box>

			{/* Keep the legacy table mirror behind testing mode while draining its
			    wrapper-backed controls onto direct v2 primitives. */}
			{showTesting() && isDeveloper() && (
				<>
					<Box marginTop="1200px"></Box>
					<Box w="100%">
						<RecordNav
							data={visibleBaselines}
							showToggleInactive
							showSortBy
							showRowsPerPage
							rowsPerPage={rowsPerPage}
							count={visibleRows.length}
							onToggleInactive={toggleInactiveHandler}
							onSortBy={sortByHandler}
							onChangeRowsPerPage={changeRowsPerPageHandler}
							orderBy={orderBy} // may be "updatedAtTS" / "createdAtTS" / "name" / "status_id"
							order={order}
						/>

						<Box w="100%" mb={3} boxShadow="sm" bg="white" rounded="md">
							{showTesting() && isDevTeam() && (
								<EnhancedTableToolbar numSelected={selected.length} />
							)}
							<Box overflowX="auto">
								<Table.Root
									size={dense ? "sm" : "md"}
									variant="outline"
									aria-labelledby="tableTitle"
								>
									<EnhancedTableHead
										numSelected={selected.length}
										order={order}
										orderBy={orderBy}
										onSelectAllClick={handleSelectAllClick}
										onRequestSort={handleRequestSort}
										rowCount={visibleBaselines.length}
										headCells={headCells}
										uiSize={uiSize}
										styles={styles}
									/>
									<Table.Body>
										{visibleRows.map((row, index) => {
											const isItemSelected = isSelected(row.id);

											return (
												<Table.Row
													key={row.id}
													data-selected={isItemSelected}
													style={{ cursor: "pointer" }}
													className={styles.tableBodyRow}
												>
													{showTesting() && isDevTeam() && (
														<Table.Cell>
															<Checkbox.Root
																checked={isItemSelected}
																onCheckedChange={(next) =>
																	checkHandler(
																		{
																			target: {
																				checked:
																					next === true ||
																					next?.checked === true ||
																					next?.detail?.checked ===
																						true,
																			},
																		},
																		row.id,
																	)
																}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																}}
															>
																<Checkbox.HiddenInput />
																<Checkbox.Control />
															</Checkbox.Root>
														</Table.Cell>
													)}
													{showTesting() && isDevTeam() && (
														<Table.Cell
															onClick={(event) => rowClickHandler(event, row)}
														>
															{row.uuid}
														</Table.Cell>
													)}
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
													{row.description?.trim?.() ? (
														<MetreaTooltip content={row.description}>
															<div>{row.name}</div>
														</MetreaTooltip>
													) : (
														<div>{row.name}</div>
													)}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														{row.periodLabel}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														{row.currency_code} -{" "}
														{`${row.currency_symbol} ${row.currency_unit}`}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
														style={{ width: "10%", fontSize: "0.7rem" }}
													>
														{getShortDateString(row.createdAt)}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
														style={{ width: "10%", fontSize: "0.7rem" }}
													>
														{getShortDateTimeString(row.updatedAt)}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														{row?.scenarios?.length ?? 0}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														{row?.number_of_active_items ?? 0}
													</Table.Cell>
													<Table.Cell style={{ textAlign: "center" }}>
														{pageMode !== "edit" ||
														baselineAccess(
															row,
															allUserGroups,
															Session().currUser,
														) !== 2
															? row.status_id === 1
																? "Active"
																: "Inactive"
															: ""}
														{pageMode === "edit" &&
															baselineAccess(
																row,
																allUserGroups,
																Session().currUser,
															) === 2 &&
															renderStatusSelect(row)}
													</Table.Cell>
												</Table.Row>
											);
										})}
										{emptyRows > 0 && (
											<Table.Row style={{ height: (dense ? 33 : 53) * emptyRows }}>
												<Table.Cell
													colSpan={
														(showTesting() && isDevTeam() ? 1 : 0) +
														headCells.length
													}
													style={{ borderBottom: "none", padding: 0 }}
												/>
											</Table.Row>
										)}
									</Table.Body>
								</Table.Root>
							</Box>
						</Box>

						<Box className="flex-auto justify-items-center p-1">
							<Pagination.Root
								count={visibleBaselines.length}
								pageSize={Number(rowsPerPage) || 10}
								page={pageM1}
								onPageChange={({ page }) => handleChangePageM1(undefined, page)}
							>
								<ButtonGroup variant="ghost" size="2xs">
									<Pagination.PrevTrigger asChild>
										<IconButton>
											<ChevronLeftRegular />
										</IconButton>
									</Pagination.PrevTrigger>
									<Pagination.Items
										render={(page) => (
											<IconButton variant={{ base: "ghost", _selected: "solid" }}>
												{page.value}
											</IconButton>
										)}
									/>
									<Pagination.NextTrigger asChild>
										<IconButton>
											<ChevronRightRegular />
										</IconButton>
									</Pagination.NextTrigger>
								</ButtonGroup>
							</Pagination.Root>
						</Box>

						{showTesting() && isDevTeam() && isDevSiteNow() && (
							<Switch.Root
								size="sm"
								className="m-[10px]"
								checked={dense}
								onCheckedChange={(next) =>
									handleChangeDense({
										target: {
											checked:
												next === true ||
												next?.checked === true ||
												next?.detail?.checked === true,
										},
									})
								}
							>
								<Switch.HiddenInput />
								<Switch.Control>
									<Switch.Thumb />
								</Switch.Control>
								<Switch.Label>Dense padding</Switch.Label>
							</Switch.Root>
						)}
					</Box>
				</>
			)}
		</>
	);
};

BaselineList.propTypes = {
	onSelectBaseline: PropTypes.func.isRequired,
	onChangeStatus: PropTypes.func.isRequired,
	mode: PropTypes.string,
	size: PropTypes.string,
};

export default BaselineList;
